/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Calendar, List } from "antd";
import dayjs from "dayjs";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";
import type { CalendarProps } from "antd";
import type { Dayjs } from "dayjs";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
// import DivE from "../react/DivE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getAppState, changeAppState, getUser, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Option } = Select;
const { rz } = window;

type Tday = {
  date:string, endDate?:string, title:string, icon:React.ReactNode, iconColor:string, disabled:boolean, hidden:boolean
}

type Tcalendar = {
  size:"large" | "small",
  mode:"month" | "year"
};

const RZcalendar:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZcalendar.displayName = "Calendar";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key));
  const elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes.key || rz.strMakeID(24)]), [rootAttributes.key]);
  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const calendarType = useMemo<Tcalendar>(() => {
    const typ:Tcalendar = {size: "large", mode: "month"};
    (type || "").replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["small", "large"].indexOf(tt)) { typ.size = tt }
      if (~["year", "month"].indexOf(tt)) { typ.mode = tt }
    });

    if (attributes?.size) { typ.size = attributes.size }
    return typ;
  }, [type, attributes]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.disabled, elState?.userClass, permissions.disabled]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  // const title = useMemo<string | undefined>(() => (elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng]), [elState.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const validRange = useMemo<[Dayjs, Dayjs] | undefined>(() => {
    let vldrng;
    if (elState.attributes?.validRange) {
      let dt1, dt2;
      if (rz.isArray(elState.attributes.validRange) && elState.attributes.validRange.length === 2) { dt1 = elState.attributes.validRange[0]; dt2 = elState.attributes.validRange[1] }
      else if (rz.isString(elState.attributes.validRange)) {
        const dts = elState.attributes.validRange.replace(",", "|").replace(";", "|").split("|"); if (dts.length === 2) { dt1 = dts[0]; dt2 = dts[1] }
      }

      if (rz.isString(dt1) && rz.isString(dt2)) { const frmt = rz.detectStrDateFormat(dt1, true); vldrng = [dayjs(dt1, frmt), dayjs(dt2, frmt)] }
      else if (rz.isDate(dt1) && rz.isDate(dt2)) { vldrng = [dayjs(dt1), dayjs(dt2)] }
    }
    return vldrng;
  }, [elState.attributes.validRange]);

  const listData = useMemo<Array<Tday>>(() => {
    if (elState.items && elState.items.length) {
      return elState.items.map((ch, c) => {
        const perm1 = fnc.checkPermissions(user, {...ch?.attributes, rootAttributes: {key: rootAttributes.key}});
        return {
          date: ch.date || ch.key || "",
          endDate: ch.endDate || "",
          icon: (<AppIcons key={(ch.key || (key + "-" + c)) + "-icon"} icopath={ch?.icon || ""} attrs={{className: ((ch["title_" + lng] || ch.title || ch["title_" + rlng]) ? "rz-margin-right-5" : ""), color: ch.iconColor}} />),
          title: ch["title_" + lng] || ch.title || ch["title_" + rlng] || "",
          disabled: (perm1.disabled === true || appState[ch.fullPath]?.disabled),
          hidden: (perm1.visible === false || appState[ch.fullPath]?.visible === false)
        }
      }).filter((ch) => (ch.date && ch.hidden !== true));
    }
    if (content && content.length) {
      return content.map((ch, c) => {
        const perm1 = fnc.checkPermissions(user, {...ch?.attributes, rootAttributes: {key: rootAttributes.key}});
        return {
          date: ch.attributes.date || ch.attributes.key || "",
          endDate: ch.attributes.endDate || "",
          // eslint-disable-next-line max-len
          icon: (<AppIcons key={(ch.attributes?.key || (key + "-" + c)) + "-icon"} icopath={ch.attributes?.icon || ""} attrs={{className: ((ch.attributes["title_" + lng] || ch.attributes.title || ch.attributes["title_" + rlng]) ? "rz-margin-right-5" : ""), color: ch.attributes.iconColor}} />),
          title: ch.attributes["title_" + lng] || ch.attributes.title || ch.attributes["title_" + rlng] || "",
          disabled: (perm1.disabled === true || appState[ch.fullPath]?.disabled),
          hidden: (perm1.visible === false || appState[ch.fullPath]?.visible === false)
        }
      }).filter((ch) => (ch.date && ch.hidden !== true));
    }
    return [];
  }, [appState, content, elState.items, key, lng, rlng, rootAttributes.key, user]);

  const onSelect = useCallback((date:Dayjs, info:{source:"year" | "month" | "date" | "customize"}) => {
    if (fnc.config?.isDev) console.log({action: "<Calendar key='" + key + "'> -> onSelect()", mode: info.source, value: date.format("YYYY-MM-DD")});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: date.format("YYYY-MM-DD")}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange || attributes.onSelect || attributes.onselect) && rootNamespace[attributes.onChange || attributes.onchange || attributes.onSelect || attributes.onselect]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange || attributes.onSelect || attributes.onselect];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, mode: info.source, value: date.format("YYYY-MM-DD"), safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, mode: info.source, value: date.format("YYYY-MM-DD"), safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, navigate, safe]);

  const onPanelChange = useCallback((date:Dayjs, mode:string) => {
    if (fnc.config?.isDev) console.log({action: "<Calendar key='" + key + "'> -> onPanelChange()", mode, value: date.format("YYYY-MM-DD")});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, attributes: {...elState.attributes, mode}}}));
    if (rootNamespace && (attributes.onpanelchange || attributes.onPanelChange) && rootNamespace[attributes.onpanelchange || attributes.onPanelChange]) {
      const clientFNC = rootNamespace[attributes.onpanelchange || attributes.onPanelChange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, mode, value: date.format("YYYY-MM-DD"), safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
          if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, mode, value: date.format("YYYY-MM-DD"), safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
        if (ret && ret.navigate) { navigate(ret.navigate.to, {replace: Boolean(ret.navigate.replace)}); console.log("navigate(" + ret.navigate.to + ")") }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, navigate, safe]);

  const cellRender = useCallback((value:Dayjs, info) => {
    const dateFormat = (info.type === "date" ? "YYYY-MM-DD" : "YYYY-MM");
    const items:Array<Tday> = [];
    listData.forEach((ii) => {
      const dt1 = (ii.date || "").split(" ")[0], dt2 = (ii?.endDate || "").split(" ")[0], dm1 = dt1.split("-"), dm2 = dt2.split("-");
      const curDate = value.format(dateFormat);
      if (info.type === "date") {
        if (dt1 === curDate || (dt2 && curDate >= dt1 && curDate <= dt2)) { items.push(ii) }
      }
      else if (info.type === "month") {
        if ((dm1[0] + "-" + dm1[1]) === curDate || (dm2[1] && curDate >= (dm1[0] + "-" + dm1[1]) && curDate <= (dm2[0] + "-" + dm2[1]))) { items.push(ii) }
      }
    });
    if (!items || !items.length) { return (<React.Fragment key={value.format(dateFormat)} />); /* info.originNode */ }
    return (
      <List key={value.format(dateFormat)}
        size="small"
        dataSource={items}
        renderItem={(item, i) => (<List.Item key={item.date + "_" + i}>{item.icon}{item.title}</List.Item>)}
      />
    );
    /*
    if (info.type === "date") { return dateCellRender(current) }
    if (info.type === "month") { return monthCellRender(current) }
    return info.originNode;
    */
  }, [listData]);

  return (
    <Calendar
      key={key} style={styl} className={clsu.join(" ")} uk-tooltip={tooltip}
      value={elState.value ? dayjs(rz.dateFromString(elState.value)) : undefined}
      validRange={validRange}
      mode={elState.attributes?.mode || calendarType.mode}
      fullscreen={Boolean(calendarType.size === "large")}
      cellRender={cellRender}
      onSelect={onSelect}
      onPanelChange={onPanelChange}
    />
  );
}

export default RZcalendar;
/* eslint-enable @typescript-eslint/no-unused-vars */
